import { useEffect, useSyncExternalStore } from 'react';
import {
  setEditorTarget,
  setInputTarget,
  resetVariableTarget,
  hasVariableTarget,
  subscribeVariableTarget,
} from './variableTargetStore';

// Editor events that move or settle the caret. `blur` captures the position the
// moment focus leaves the editor (e.g. towards the Variables panel).
const EDITOR_EVENTS = 'focus blur keyup mouseup NodeChange SelectionChange';

function isSubjectInput(el) {
  return (
    (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) &&
    el.dataset.variableTarget === 'true'
  );
}

// Binds caret tracking for the TinyMCE editor and every subject input. Returns a
// cleanup function. Intended to be mounted once (from VariablesCard).
function bindTracking() {
  let boundEditor = null;
  let pollId = null;

  const onEditorEvent = () => {
    if (boundEditor) setEditorTarget(boundEditor);
  };

  // Make this editor the default insertion point. Relying on the editor's own
  // init `NodeChange` is not enough: an empty new template fires no NodeChange,
  // so `+` would stay disabled until the user clicks a field. Setting it here
  // (after init, when the selection is ready) keeps `+` active on entry for both
  // new and existing templates.
  const setEditorDefault = (editor) => {
    if (editor.initialized) setEditorTarget(editor);
    else editor.on('init', () => setEditorTarget(editor));
  };

  const bindEditor = (editor) => {
    if (!editor || boundEditor === editor) return;
    if (boundEditor) boundEditor.off(EDITOR_EVENTS, onEditorEvent);
    boundEditor = editor;
    editor.on(EDITOR_EVENTS, onEditorEvent);
    // New editor = new template context: forget the previous template's position,
    // then default the insertion point to this editor.
    resetVariableTarget();
    setEditorDefault(editor);
  };

  const onAddEditor = ({ editor }) => bindEditor(editor);

  const attach = () => {
    window.tinymce.on('AddEditor', onAddEditor);
    if (window.tinymce.activeEditor) bindEditor(window.tinymce.activeEditor);
  };

  if (window.tinymce) {
    attach();
  } else {
    pollId = setInterval(() => {
      if (window.tinymce) {
        clearInterval(pollId);
        pollId = null;
        attach();
      }
    }, 200);
  }

  // Subject inputs: track the caret on any activity that can move it.
  const onInputActivity = (e) => {
    if (isSubjectInput(e.target)) setInputTarget(e.target);
  };

  document.addEventListener('focusin', onInputActivity);
  document.addEventListener('keyup', onInputActivity);
  document.addEventListener('mouseup', onInputActivity);
  document.addEventListener('select', onInputActivity, true);

  return () => {
    if (pollId) clearInterval(pollId);
    if (boundEditor) boundEditor.off(EDITOR_EVENTS, onEditorEvent);
    window.tinymce?.off?.('AddEditor', onAddEditor);
    document.removeEventListener('focusin', onInputActivity);
    document.removeEventListener('keyup', onInputActivity);
    document.removeEventListener('mouseup', onInputActivity);
    document.removeEventListener('select', onInputActivity, true);
  };
}

// Tracks the last caret position and returns whether a variable can be inserted
// (i.e. a saved target exists). The `+` button stays enabled while the user
// works in the search box or list, because the target persists across focus.
export function useVariableTarget() {
  useEffect(() => bindTracking(), []);
  return useSyncExternalStore(subscribeVariableTarget, hasVariableTarget, hasVariableTarget);
}
